import { useAuth } from "@/contexts/AuthContext";
import { getErrorMessage } from "@/utils/errorUtils";
import { type ReactElement, useEffect, useState } from "react";
import { toast } from "react-hot-toast";

import { FirmSettingsView } from "./FirmSettingsView";
import { useFirmSettings } from "./useFirmSettings";

export const FirmSettings = (): ReactElement => {
  const { refreshFirmContext } = useAuth();
  const { firmSettings, isLoading, isError, saveFirmSettings } = useFirmSettings();
  const [editedFirmName, setEditedFirmName] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const firmName = editedFirmName ?? firmSettings?.name ?? "";
  const canSave = firmSettings !== null && firmName.trim() !== "";

  useEffect(() => {
    if (isError) toast.error("Failed to load firm settings");
  }, [isError]);

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      await saveFirmSettings({ name: firmName });
      setEditedFirmName(null);
    } catch (error) {
      console.error("[FirmSettings] Failed to save firm settings:", error);
      toast.error(getErrorMessage(error));
      return;
    } finally {
      setIsSaving(false);
    }
    toast.success("Settings saved successfully");
    await refreshFirmContext(true);
  };

  return (
    <FirmSettingsView
      isLoading={isLoading}
      firmName={firmName}
      isSaving={isSaving}
      canSave={canSave}
      onFirmNameChange={setEditedFirmName}
      onSave={() => void handleSave()}
    />
  );
};
